import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Reactions from '../components/Reactions';
import { apiFetch } from '../services/apiClient';
import { linkForItem } from '../utils/deepLink';

// Mobile "Daily Updates" — one roof over everything that says what happened today:
//  • the audio Daily Podcast (its own screen)
//  • the written daily updates, per PM / Shop / each Work Flow
//  • two firehoses: All Activity, and All Daily Updates
// Exec/office see every channel; a PM sees their own update channel and the work-flow
// channels — the same rule the podcast uses. The two "all" feeds are company-wide.
const NAVY = '#1e3a5f';
const ORANGE = '#e87722';

const fmtWhen = iso => {
  if (!iso) return '';
  const d = new Date(iso); if (isNaN(d)) return '';
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay
    ? d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' · ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};
const chanIcon = a => (a === 'pm' ? '👤' : a === 'shop' ? '🔧' : a === 'workflow' ? '🛠️' : '📋');
// Strip the "posted a daily update:"/"daily update:" prefix so a row reads as the note.
const cleanAction = s => String(s || '').replace(/^.*?daily update:\s*/i, '').replace(/^"(.*)"$/, '$1');

function TopBar({ title, sub, onBack }) {
  return (
    <div className="top-bar">
      <button className="back-btn-inline" onClick={onBack} aria-label="Back">
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M11 4L6 9l5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </button>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="top-bar-title">{title}</div>
        {sub && <div className="top-bar-sub">{sub}</div>}
      </div>
    </div>
  );
}

// One tappable feed — either written updates (full notes) or activity lines.
const ROLE_LABEL = { ProjectManager: 'PM', FieldSupervisor: 'Field Super', OfficeStaff: 'Office', Accounting: 'Accounting', Executive: 'Executive', Admin: 'Admin', Worker: 'Field', Shop: 'Shop' };
const roleLabel = r => (r ? (ROLE_LABEL[r] || String(r).replace(/([a-z])([A-Z])/g, '$1 $2')) : 'Unknown');

function Feed({ title, sub, url, kind, onBack }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  // Written updates can be read newest-first, or grouped by who wrote them, or by
  // what they are (PM / field / office) — Ryan asked for the two sorts (Sep 2026).
  const [sort, setSort] = useState('newest');   // 'newest' | 'name' | 'role'
  const [roleFilter, setRoleFilter] = useState('all');
  // Type-to-find (Pat, Sep 29 2026): the shop feed was scroll-only. Matches the writer, the
  // job, the role and the words of the note — every word typed has to appear somewhere.
  const [q, setQ] = useState('');
  useEffect(() => {
    apiFetch(url).then(d => setRows(Array.isArray(d) ? d : [])).catch(e => setErr(e?.error || 'Could not load this.'));
  }, [url]);
  const isUpd = kind === 'update';
  const roles = isUpd && rows ? [...new Set(rows.map(r => r.author_role || 'Unknown'))].sort() : [];
  const words = q.toLowerCase().split(/\s+/).filter(Boolean);
  const hay = r => [isUpd ? r.author_name : r.actor_name, isUpd ? r.notes : cleanAction(r.action), r.job_number, r.job_name, isUpd ? roleLabel(r.author_role) : ''].join(' ').toLowerCase();
  const shown = (rows || [])
    .filter(r => !isUpd || roleFilter === 'all' || (r.author_role || 'Unknown') === roleFilter)
    .filter(r => !words.length || (h => words.every(w => h.includes(w)))(hay(r)))
    .slice()
    .sort((a, b) => {
      if (!isUpd || sort === 'newest') return String(b.created_at || '').localeCompare(String(a.created_at || ''));
      if (sort === 'name') return String(a.author_name || '').localeCompare(String(b.author_name || '')) || String(b.created_at || '').localeCompare(String(a.created_at || ''));
      return roleLabel(a.author_role).localeCompare(roleLabel(b.author_role)) || String(a.author_name || '').localeCompare(String(b.author_name || '')) || String(b.created_at || '').localeCompare(String(a.created_at || ''));
    });

  const go = r => {
    const target = kind === 'update'
      ? (r.project_id ? `/admin/new-projects/${r.project_id}` : null)
      : linkForItem({ scope: r.scope, projectId: r.project_id || r.scope_id, data: r.meta });
    if (target) navigate(target);
  };

  return (
    <div className="page" style={{ paddingBottom: 30 }}>
      <TopBar title={title} sub={sub} onBack={onBack} />
      <div style={{ padding: '12px 16px' }}>
        {err && <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', color: '#dc2626', fontSize: 14 }}>{err}</div>}
        {!rows && !err && <div style={{ color: '#9ca3af', fontSize: 14 }}>Loading…</div>}
        {rows && rows.length === 0 && (
          <div style={{ textAlign: 'center', color: '#9ca3af', padding: '44px 10px' }}>
            <div style={{ fontSize: 30, marginBottom: 8 }}>📋</div>Nothing here yet.
          </div>
        )}
        {rows && rows.length > 1 && (
          <div style={{ position: 'relative', marginBottom: 10 }}>
            <input type="search" value={q} onChange={e => setQ(e.target.value)} placeholder={isUpd ? 'Search updates — name, job, words…' : 'Search — name, job, words…'} aria-label="Search"
              style={{ width: '100%', boxSizing: 'border-box', padding: '10px 36px 10px 14px', borderRadius: 12, border: '1.5px solid #e5e7eb', fontSize: 15, background: '#fff' }} />
            {q && <button onClick={() => setQ('')} aria-label="Clear search" style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', border: 'none', background: '#f3f4f6', borderRadius: 999, width: 24, height: 24, fontSize: 13, cursor: 'pointer', color: '#374151' }}>✕</button>}
          </div>
        )}
        {rows && rows.length > 0 && shown.length === 0 && (
          <div style={{ textAlign: 'center', color: '#9ca3af', padding: '24px 10px', fontSize: 14 }}>Nothing matches “{q}”.</div>
        )}
        {isUpd && rows && rows.length > 1 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, fontWeight: 800, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.4 }}>Sort</span>
              {[['newest', 'Newest'], ['name', 'By name'], ['role', 'By role']].map(([k, l]) => (
                <button key={k} onClick={() => setSort(k)}
                  style={{ padding: '6px 12px', borderRadius: 999, border: 'none', fontSize: 13, fontWeight: 700, cursor: 'pointer', background: sort === k ? '#111' : '#f3f4f6', color: sort === k ? ORANGE : '#374151' }}>{l}</button>
              ))}
            </div>
            {roles.length > 1 && (
              <div style={{ display: 'flex', gap: 6, overflowX: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: 2 }}>
                {[['all', 'Everyone'], ...roles.map(r => [r, roleLabel(r)])].map(([k, l]) => (
                  <button key={k} onClick={() => setRoleFilter(k)}
                    style={{ whiteSpace: 'nowrap', padding: '5px 11px', borderRadius: 999, border: '1px solid ' + (roleFilter === k ? NAVY : '#e5e7eb'), fontSize: 12.5, fontWeight: 700, cursor: 'pointer', background: roleFilter === k ? NAVY : '#fff', color: roleFilter === k ? '#fff' : '#374151' }}>{l}</button>
                ))}
              </div>
            )}
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {shown.map((r, i) => {
            const who = kind === 'update' ? r.author_name : r.actor_name;
            const text = kind === 'update' ? r.notes : cleanAction(r.action);
            const job = r.job_number || r.job_name;
            // Grouped sorts get a heading where the group changes, so the list reads in sections.
            const prev = shown[i - 1];
            const groupKey = isUpd && sort === 'name' ? (r.author_name || 'Someone') : isUpd && sort === 'role' ? roleLabel(r.author_role) : null;
            const prevKey = prev ? (sort === 'name' ? (prev.author_name || 'Someone') : roleLabel(prev.author_role)) : null;
            const heading = groupKey && groupKey !== prevKey ? groupKey : null;
            return (
              <React.Fragment key={r.id || i}>
              {heading && <div style={{ fontSize: 11, fontWeight: 800, color: NAVY, textTransform: 'uppercase', letterSpacing: 0.5, margin: i === 0 ? '0 0 -2px' : '10px 0 -2px' }}>{heading}</div>}
              <div role="button" tabIndex={0} onClick={() => go(r)} onKeyDown={e => { if (e.key === 'Enter') go(r); }}
                style={{ display: 'block', width: '100%', boxSizing: 'border-box', textAlign: 'left', background: '#fff', border: '1.5px solid #ececec', borderRadius: 14, padding: '12px 14px', cursor: 'pointer' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 3, alignItems: 'baseline' }}>
                  <span style={{ fontSize: 13.5, fontWeight: 800, color: '#111', minWidth: 0 }}>{who || 'Someone'}{isUpd && r.author_role && <span style={{ marginLeft: 6, fontSize: 10.5, fontWeight: 700, color: '#6b7280', background: '#f3f4f6', borderRadius: 999, padding: '1px 7px' }}>{roleLabel(r.author_role)}</span>}</span>
                  <span style={{ fontSize: 11.5, color: '#9ca3af', flexShrink: 0 }}>{fmtWhen(r.created_at)}</span>
                </div>
                {/* Number and name together (Ryan, Oct 3 2026): "WO26091 · Taylor House - Bitnor House service". */}
                {job && <div style={{ fontSize: 11.5, fontWeight: 700, color: ORANGE, marginBottom: 3 }}>{/* The job's number AND name (Ryan, Oct 12 2026 — "2628" alone said nothing): when the name already
                    starts with the number ("2628 - RTC Prater Way"), the name says both. */}
                {r.job_number && r.job_name ? (String(r.job_name).startsWith(String(r.job_number)) ? r.job_name : `${r.job_number} · ${r.job_name}`) : job}</div>}
                <div style={{ fontSize: 14, color: '#374151', lineHeight: 1.5 }}>{text}</div>
                <Reactions type={isUpd ? 'update' : r.react?.type} id={isUpd ? r.id : r.react?.id} reactions={r.reactions} />
              </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function DailyUpdates() {
  const navigate = useNavigate();
  const [channels, setChannels] = useState(null);
  const [feed, setFeed] = useState(null);   // a Feed descriptor, or null for the hub
  const [err, setErr] = useState('');

  useEffect(() => {
    apiFetch('/daily/channels')
      .then(d => setChannels(d && typeof d === 'object' ? d : { pms: [], workflows: [] }))
      .catch(e => setErr(e?.error || 'Could not load the channels.'));
  }, []);

  if (feed) return <Feed {...feed} onBack={() => setFeed(null)} />;

  const Row = ({ icon, label, sub, onClick, accent }) => (
    <button onClick={onClick}
      style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left', background: '#fff', border: '1.5px solid #ececec', borderRadius: 14, padding: '13px 14px', cursor: 'pointer' }}>
      <span style={{ flexShrink: 0, width: 42, height: 42, borderRadius: 12, background: accent || '#eef2f7', color: accent ? '#fff' : NAVY, fontSize: 19, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 15, fontWeight: 800, color: '#111' }}>{label}</span>
        {sub && <span style={{ display: 'block', fontSize: 12, color: '#8a8a8a' }}>{sub}</span>}
      </span>
      <span style={{ flexShrink: 0, color: '#c4c4c4', fontSize: 20 }}>›</span>
    </button>
  );

  const Section = ({ title }) => (
    <div style={{ fontSize: 11, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, margin: '18px 0 8px' }}>{title}</div>
  );

  const pms = channels?.pms || [];
  const workflows = channels?.workflows || [];
  const shop = channels?.shop || null;
  const openUpdate = (audience, key, title) => setFeed({ title, sub: 'Daily updates', kind: 'update', url: `/daily/updates?audience=${audience}${key ? `&key=${key}` : ''}` });
  const openScope = (audience, key, title) => setFeed({ title, sub: 'Daily updates', kind: 'activity', url: `/daily/updates?audience=${audience}&key=${key}` });

  return (
    <div className="page" style={{ paddingBottom: 30 }}>
      <div className="top-bar">
        <button className="back-btn-inline" onClick={() => navigate('/dashboard')} aria-label="Back">
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M11 4L6 9l5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="top-bar-title">📋 Daily Updates</div>
          <div className="top-bar-sub">The podcast, the updates, and the activity</div>
        </div>
      </div>

      <div style={{ padding: '10px 16px' }}>
        {err && <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', color: '#dc2626', fontSize: 14 }}>{err}</div>}

        {/* The podcast keeps its own home; this is the way in. */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6 }}>
          <Row icon="🎙️" label="Daily Podcast" sub="Listen to the morning briefs" accent={NAVY} onClick={() => navigate('/podcasts')} />
        </div>

        <Section title="Everything" />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <Row icon="🌀" label="All Activity" sub="Every change across the company" onClick={() => setFeed({ title: 'All Activity', sub: 'Company-wide', kind: 'activity', url: '/daily/all-activity' })} />
          <Row icon="📝" label="All Daily Updates" sub="Every written update, everyone" onClick={() => setFeed({ title: 'All Daily Updates', sub: 'Company-wide', kind: 'update', url: '/daily/all-updates' })} />
        </div>

        {pms.length > 0 && (<>
          <Section title="Daily Updates · Project Managers" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {pms.map(p => (
              <Row key={p.key} icon={chanIcon('pm')} label={p.label} sub={`${p.count} update${p.count === 1 ? '' : 's'}`}
                onClick={() => openUpdate('pm', p.key, p.label)} />
            ))}
          </div>
        </>)}

        {(shop || workflows.length > 0) && (<>
          <Section title="Daily Updates · Shop & Work Flows" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {shop && <Row icon={chanIcon('shop')} label="Shop" sub={`${shop.count} update${shop.count === 1 ? '' : 's'}`} onClick={() => openScope('shop', 'shop', 'Shop')} />}
            {workflows.map(w => (
              <Row key={w.key} icon={chanIcon('workflow')} label={w.label} sub={`${w.count} update${w.count === 1 ? '' : 's'}`}
                onClick={() => openScope('workflow', w.key, w.label)} />
            ))}
          </div>
        </>)}
      </div>
    </div>
  );
}
